import React from 'react';
import styled from 'styled-components';
import { Box, TextButton } from '@99designs/ui';
import { FormattedReactMessage, FormattedMessage } from '@99designs/frontend-components/i18n';
import { Duration } from 'luxon';

import { getSupportContactUrl } from '@99designs/frontend-components/utils/platform/supportUrlHelpers';
import { BaseLink, NoWrap } from '@99designs/purgatory';
import { useCountdown } from './useCountdown';

const CenteredParagraph = styled.p`
    text-align: center;
`;

export function ResendTokenPrompt({
    resendRequestCount,
    requestNewToken,
}: {
    resendRequestCount: number;
    requestNewToken: () => void;
}) {
    const THIRTY_SECONDS_IN_MILLISECONDS = 30000;
    const { countdown, start: startCountdown, isRunning: isCountdownRunning } = useCountdown({
        startTime: THIRTY_SECONDS_IN_MILLISECONDS,
    });

    function handleResend() {
        startCountdown();
        requestNewToken();
    }

    return (
        <>
            <Box display="flex" flexDirection="row" alignItems="baseline" justifyContent="center">
                <NoWrap>
                    {getResendMessage({
                        handleResend,
                        countdown,
                        resendRequestCount,
                        isCountdownRunning,
                    })}
                </NoWrap>
            </Box>

            {resendRequestCount > 1 && (
                <Box
                    flexDirection="row"
                    alignItems="baseline"
                    justifyContent="center"
                    marginTop="halfGutter"
                    marginBottom="halfGutter"
                    paddingLeft="doubleGutter"
                    paddingRight="doubleGutter"
                >
                    <CenteredParagraph>
                        <FormattedReactMessage
                            message={__(
                                "Make sure to check your spam folder. If you still haven't received a code please <Link>contact support</Link>"
                            )}
                            replacements={{
                                Link: <BaseLink href={getSupportContactUrl(__LANG__)} />,
                            }}
                        />
                    </CenteredParagraph>
                </Box>
            )}
        </>
    );
}

function getResendMessage({
    resendRequestCount,
    countdown,
    handleResend,
    isCountdownRunning,
}: {
    resendRequestCount: number;
    countdown: number;
    handleResend: () => void;
    isCountdownRunning: boolean;
}) {
    return (
        <>
            {resendRequestCount === 0
                ? __("Didn't receive a code? ")
                : __("Still didn't receive a code? ")}
            {isCountdownRunning ? (
                <FormattedMessage
                    message={__('Resend in {countdown}')}
                    values={{
                        countdown: Duration.fromMillis(countdown).toFormat('mm:ss'),
                    }}
                />
            ) : (
                <TextButton data-testid="resend-token" onClick={handleResend}>
                    {__('Resend code')}
                </TextButton>
            )}
        </>
    );
}
